import React, { useEffect, useMemo, useState } from 'react';
import cn from 'clsx';
import { snakeCase } from 'lodash';

import Button from '../../library/button/button';
import Tooltip from '../../library/tooltip/tooltip';
import Typography from '../../library/typography/typography';
import CalculatorRegularIcon from '../../shared/icons/calculator-regular-icon';
import {
  CarBumpRegularIcon,
  CarTrafficLightRegularIcon,
  ChargingStationRegularIcon,
  ClockRegularIcon,
  CloudShowersHeavyRegularIcon,
  GasPumpRegularIcon,
  PiggyBankRegularIcon,
  RoadRegularIcon,
  SnowSkidControlRegularIcon,
  SteeringWheelRegularIcon,
  SunBrightRegularIcon,
  VolumeRegularIcon,
  WetRoadsRegularIcon
} from '../../shared/icons/icons';
import CirclesRating from '../circles-rating/circles-rating';

import {
  useAemContent,
  useFitment,
  useTreadwellPreferences
} from '../../shared/hooks/hooks';

import { formatMilesInThousands } from '../../shared/utils/format/format';
import { isWinterTire } from '../../shared/utils/product/product';
import {
  calculateDrivingPreferencesLifeExpectancy,
  calculateLifeExpectancy,
  getIsAppBasedDriver
} from '../../shared/utils/treadwell/treadwell';

import {
  CmsPromotionBannerComponent,
  CmsToolTips,
  CmsToolTipsComponent,
  FuelTypeEnum,
  Maybe,
  ProductData,
  RecommendedArticleData,
  TreadWellPriorityEnum
} from '../../shared/types/graphql-types';
import { PriorityDetails } from '../../shared/types/product';
import { AEM_CONTENT_PAGE_IDS } from '../../shared/constants/cms';
import { TREADWELL_PRIORITY } from '../../shared/constants/treadwell';

import styles from './product-first-priority-details.scss';

enum PriorityEnum {
  AppBasedDriver = 'appBasedDriver',
  NoRollingResistance = 'noRollingResistance',
  WinterTires = 'winterTires'
}

// TODO: Remove FFRecommendedArticleData when __FF_TREADWELL_GIG_ECONOMY__ is live
interface FFRecommendedArticleData extends RecommendedArticleData {
  totalCost?: Maybe<string>;
}

interface AemContentTypes {
  costPerMile: Maybe<CmsToolTips>;
  evRange: Maybe<CmsToolTips>;
  evRangePending: Maybe<CmsToolTips>;
  fuelEfficiency: Maybe<CmsToolTips>;
  fuelEfficiencyPending: Maybe<CmsToolTips>;
  handlingSteeringResponse: Maybe<CmsToolTips>;
  milesPerDollar: Maybe<CmsToolTips>;
  skidControl: Maybe<CmsToolTips>;
  stoppingDistance: Maybe<CmsToolTips>;
  yearsAndMonths: Maybe<CmsToolTips>;
}

interface ProductFirstPriorityDetailsProps {
  dataReportUrl?: string;
  hideLink?: boolean;
  onClick?: () => void;
  product: ProductData;
}

function ProductFirstPriorityDetails(props: ProductFirstPriorityDetailsProps) {
  const {
    dataReportUrl = 'https://www.discounttire.com/404',
    hideLink = false,
    onClick,
    product
  } = props;

  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();

  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );

  const contentMap: AemContentTypes = useMemo(() => {
    const priorityKeys = {
      costPerMile: null,
      evRange: null,
      evRangePending: null,
      fuelEfficiency: null,
      fuelEfficiencyPending: null,
      handlingSteeringResponse: null,
      milesPerDollar: null,
      skidControl: null,
      stoppingDistance: null,
      yearsAndMonths: null
    };

    if (Object.keys(aemTooltipsContent).length > 0) {
      Object.keys(priorityKeys).forEach(key => {
        const tooltipContent = aemTooltipsContent[snakeCase(key)];

        if (tooltipContent) {
          priorityKeys[key] = tooltipContent;
        }
      });
    }

    return priorityKeys;
  }, [aemTooltipsContent]);

  const normalizedTreadwellPreferences: {
    [key: string]: CmsPromotionBannerComponent;
  } = useMemo(
    () =>
      treadwellPreferences?.selectedCustomDrivingPriorities?.reduce(
        (acc, priority) => {
          if (priority) {
            acc[priority.id] = priority;
          }
          return acc;
        },
        {}
      ) || {},
    [treadwellPreferences]
  );

  const { selectedDriverType } = treadwellPreferences || {};
  const { selectedFitment } = useFitment();
  const isEV = selectedFitment?.fuelType === FuelTypeEnum.Bev;
  const isAppBasedDriver = getIsAppBasedDriver();

  const { dryRating, rollingResistanceRating, wetRating } =
    product?.recommendationData?.stopping || product?.tdsData?.stopping || {};
  const { comfort, handling, quiet, wetHandling } =
    product?.recommendationData?.rideRating ||
    product?.tdsData?.rideRating ||
    {};
  const { median: medianMileage, min: minMileage } =
    product?.recommendationData?.mileage || product?.tdsData?.mileage || {};

  const { snowBrakeFeet, snowBrakeRating, snowHandlingRating } =
    product?.recommendationData?.winter || product?.tdsData?.winter || {};

  const {
    tireLifeMonths: drivingPreferencesTireLifeMonths,
    tireLifeYears: drivingPreferencesTireLifeYears
  } = calculateDrivingPreferencesLifeExpectancy(product, treadwellPreferences);
  const { tireLifeMonths, tireLifeYears } = calculateLifeExpectancy(
    product,
    15 * 1000,
    15 * 1000
  );

  const { milesPerDollar, totalCost } =
    (product?.recommendationData as FFRecommendedArticleData) || {};

  const actualPrice = product?.mapPrice?.value ?? product?.price?.value;
  const actualMilesPerDollar =
    milesPerDollar ?? Math.round((medianMileage || 0) / (actualPrice || 1));
  const costPerMile = isNaN(Number(totalCost))
    ? 0
    : (Number(totalCost) / 50000) * 100;

  const priorityDetailsArr: PriorityDetails[] = useMemo(
    () => [
      {
        id: TreadWellPriorityEnum.StoppingDistance,
        ratingDescription1: (
          <div>
            <SunBrightRegularIcon className={styles['icon']} />
            Dry Road Rating
          </div>
        ),
        ratingDescription2: (
          <div>
            <CloudShowersHeavyRegularIcon className={styles['icon']} />
            Wet Road Rating
          </div>
        ),
        ratingValue1: dryRating,
        ratingValue2: wetRating,
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.STOPPING_DISTANCE]
            ?.name || 'Stopping & Traction',
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.STOPPING_DISTANCE]
            ?.imageAltText ||
          'How well tires grip in dry, wet and winter weather.'
      },
      {
        id: TreadWellPriorityEnum.ComfortAndNoise,
        ratingDescription1: (
          <div>
            <VolumeRegularIcon className={styles['icon']} />
            Quiet Ride
          </div>
        ),
        ratingDescription2: (
          <div>
            <CarBumpRegularIcon className={styles['icon']} />
            Comfort
          </div>
        ),
        ratingValue1: quiet,
        ratingValue2: comfort,
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.COMFORT_AND_NOISE]
            ?.name,
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.COMFORT_AND_NOISE]
            ?.imageAltText
      },
      {
        id: TreadWellPriorityEnum.FuelEfficiency,
        ratingDescription1: (
          <div>
            {isEV ? (
              <ChargingStationRegularIcon className={styles['icon']} />
            ) : (
              <GasPumpRegularIcon className={styles['icon']} />
            )}
            {isEV
              ? contentMap.evRange?.title
              : contentMap.fuelEfficiency?.title}
          </div>
        ),
        ratingDescription2: (
          <div>
            <VolumeRegularIcon className={styles['icon']} />
            Quiet Ride
          </div>
        ),
        ratingValue1: rollingResistanceRating,
        ratingValue2: quiet,
        title: isEV
          ? contentMap.evRange?.title
          : contentMap.fuelEfficiency?.title,
        tooltip: isEV
          ? contentMap.evRange?.tipText
          : contentMap.fuelEfficiency?.tipText
      },
      {
        id: TreadWellPriorityEnum.Handling,
        ratingDescription1: (
          <div>
            <SteeringWheelRegularIcon className={styles['icon']} />
            Steering Response
          </div>
        ),
        ratingDescription2: (
          <div>
            <WetRoadsRegularIcon className={styles['icon']} />
            Wet Handling Response
          </div>
        ),
        ratingValue1: handling,
        ratingValue2: wetHandling,
        title: 'Steering Precision',
        tooltip: contentMap.handlingSteeringResponse?.tipText
      },
      {
        id: TreadWellPriorityEnum.LifeOfTire,
        noRatingDescription1: (
          <Typography className={styles['sub-description']} tag="div">
            <div>
              <ClockRegularIcon className={styles['icon']} />
              Life of Tire
            </div>
            <div>
              <strong>{tireLifeYears}</strong>yr{' '}
              <strong>{tireLifeMonths}</strong>mo
            </div>
          </Typography>
        ),
        noRatingDescription2: actualMilesPerDollar && (
          <Typography className={styles['sub-description']} tag="div">
            <div>
              <PiggyBankRegularIcon className={styles['icon']} />
              {contentMap.milesPerDollar?.title}
              {contentMap.milesPerDollar?.tipText && (
                <Tooltip
                  className={styles['tooltip']}
                  content={contentMap.milesPerDollar.tipText}
                />
              )}
            </div>
            <div>
              <strong>{actualMilesPerDollar}</strong>
            </div>
          </Typography>
        ),
        title:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.LIFE_OF_TIRE]?.name,
        tooltip:
          normalizedTreadwellPreferences[TREADWELL_PRIORITY.LIFE_OF_TIRE]
            ?.imageAltText
      },
      {
        id: PriorityEnum.NoRollingResistance,
        pendingMsg: (
          <div>
            {isEV ? (
              <ChargingStationRegularIcon className={styles['icon']} />
            ) : (
              <GasPumpRegularIcon className={styles['icon']} />
            )}
            {isEV
              ? contentMap.evRangePending?.title
              : contentMap.fuelEfficiencyPending?.title}
            <Tooltip
              className={styles['tooltip']}
              content={
                isEV
                  ? contentMap.evRangePending?.tipText
                  : contentMap.fuelEfficiencyPending?.tipText
              }
            />
          </div>
        ),
        ratingDescription2: (
          <div>
            <VolumeRegularIcon className={styles['icon']} />
            Quiet Ride
          </div>
        ),
        ratingValue2: quiet,
        title: isEV
          ? contentMap.evRangePending?.title
          : contentMap.fuelEfficiencyPending?.title,
        tooltip: isEV
          ? contentMap.evRangePending?.tipText
          : contentMap.fuelEfficiencyPending?.tipText
      },
      {
        id: PriorityEnum.AppBasedDriver,
        noRatingDescription1: (
          <Typography className={styles['sub-description']} tag="div">
            <div>
              <CalculatorRegularIcon className={styles['icon']} />
              {contentMap.costPerMile?.title || 'Cost Per Mile'}
            </div>
            <div>
              <Typography size="small">
                {costPerMile?.toFixed(2)} cents
              </Typography>
            </div>
          </Typography>
        ),
        noRatingDescription2: (
          <Typography className={styles['sub-description']} tag="div">
            <div>
              <ClockRegularIcon className={styles['icon']} />
              Life of Tire
            </div>
            <div>
              <strong>{drivingPreferencesTireLifeYears}</strong>yr{' '}
              <strong>{drivingPreferencesTireLifeMonths}</strong>mo
            </div>
          </Typography>
        ),
        title: selectedDriverType?.name || 'App-Based Driver'
      },
      {
        id: PriorityEnum.WinterTires,
        ratingDescription1: (
          <div>
            <SnowSkidControlRegularIcon className={styles['icon']} />
            {contentMap.skidControl?.title || 'Snow Skid Control'}
            {contentMap.skidControl?.tipText && (
              <Tooltip
                className={styles['tooltip']}
                content={contentMap.skidControl.tipText}
              />
            )}
          </div>
        ),
        ...(snowBrakeRating != null
          ? {
              ratingDescription2: (
                <div>
                  <CarTrafficLightRegularIcon className={styles['icon']} />
                  {contentMap.stoppingDistance?.title ||
                    'Snow Stopping Distance'}
                  {contentMap.stoppingDistance?.tipText && (
                    <Tooltip
                      className={styles['tooltip']}
                      content={contentMap.stoppingDistance.tipText}
                    />
                  )}
                </div>
              ),
              ratingValue2: snowBrakeRating
            }
          : {
              noRatingDescription2: (
                <Typography className={styles['sub-description']} tag="div">
                  <div>
                    <CarTrafficLightRegularIcon className={styles['icon']} />
                    {contentMap.stoppingDistance?.title ||
                      'Snow Stopping Distance'}
                    {contentMap.stoppingDistance?.tipText && (
                      <Tooltip
                        className={styles['tooltip']}
                        content={contentMap.stoppingDistance.tipText}
                      />
                    )}
                  </div>
                  <div>{snowBrakeFeet} Feet</div>
                </Typography>
              )
            }),
        ratingValue1: snowHandlingRating,
        title: 'Snow & Ice',
        tooltip:
          'Winter includes ice, slush, snow and temperatures consistently below 45F'
      }
    ],
    [
      actualMilesPerDollar,
      comfort,
      contentMap,
      costPerMile,
      drivingPreferencesTireLifeMonths,
      drivingPreferencesTireLifeYears,
      dryRating,
      handling,
      isEV,
      normalizedTreadwellPreferences,
      quiet,
      rollingResistanceRating,
      selectedDriverType,
      snowBrakeFeet,
      snowBrakeRating,
      snowHandlingRating,
      tireLifeMonths,
      tireLifeYears,
      wetHandling,
      wetRating
    ]
  );
  const [detailsObj, setDetailsObj] = useState<PriorityDetails>(
    priorityDetailsArr[0]
  );

  useEffect(() => {
    function getNumber1PriorityId() {
      let priorityId: TreadWellPriorityEnum | PriorityEnum =
        isWinterTire(product) && snowBrakeFeet && snowHandlingRating
          ? PriorityEnum.WinterTires
          : TreadWellPriorityEnum.StoppingDistance;

      if (treadwellPreferences) {
        if (isAppBasedDriver) {
          priorityId = PriorityEnum.AppBasedDriver;
        } else if (
          treadwellPreferences.showWinterTires &&
          snowBrakeFeet &&
          snowHandlingRating
        ) {
          priorityId = PriorityEnum.WinterTires;
        } else if (treadwellPreferences.isCustomDriverTypeShowing) {
          const customDriverType =
            treadwellPreferences.selectedCustomDrivingPriorities as CmsPromotionBannerComponent[];
          priorityId = customDriverType[0].id as TreadWellPriorityEnum;
        } else {
          const driverType =
            treadwellPreferences.selectedDriverType as CmsToolTipsComponent;
          const tooltips = driverType?.tooltips as CmsToolTips[];
          priorityId = tooltips?.[0]?.id as TreadWellPriorityEnum;
        }
      }

      if (
        priorityId === TreadWellPriorityEnum.FuelEfficiency &&
        rollingResistanceRating === 0
      ) {
        priorityId = PriorityEnum.NoRollingResistance;
      }

      return priorityId;
    }

    const detailsObj = priorityDetailsArr.filter(
      item => item.id === getNumber1PriorityId()
    )[0];

    setDetailsObj(detailsObj);
  }, [
    isAppBasedDriver,
    isEV,
    priorityDetailsArr,
    product,
    rollingResistanceRating,
    snowBrakeFeet,
    snowHandlingRating,
    treadwellPreferences
  ]);

  if (!detailsObj) {
    return null;
  }

  return (
    <div className={cn(styles['container'], 'product-first-priority-details')}>
      <div className={styles['header']}>
        <div className={styles['treadwell-logo']} />
        <Typography className={styles['title']} tag="span">
          {isTreadwellFlow && !treadwellPreferences?.showWinterTires
            ? "Why It's a Match"
            : detailsObj.title}
          <Tooltip
            className={styles['tooltip']}
            content={
              isTreadwellFlow && !treadwellPreferences?.showWinterTires ? (
                <>
                  Based on your top priority as&nbsp;
                  <strong>{detailsObj.title}</strong>, we found tires that score
                  highly in categories you care about.
                </>
              ) : (
                detailsObj.tooltip
              )
            }
          />
        </Typography>
      </div>
      <div className={styles['footer']}>
        {detailsObj.pendingMsg && (
          <Typography className={styles['sub-description']} tag="div">
            {detailsObj.pendingMsg}
          </Typography>
        )}
        {Boolean(detailsObj.ratingValue1) && (
          <Typography className={styles['sub-description']} tag="div">
            {detailsObj.ratingDescription1}
            <CirclesRating rating={detailsObj.ratingValue1} />
          </Typography>
        )}
        {Boolean(detailsObj.ratingValue2) && (
          <Typography className={styles['sub-description']} tag="div">
            {detailsObj.ratingDescription2}
            <CirclesRating rating={detailsObj.ratingValue2} />
          </Typography>
        )}
        {detailsObj.noRatingDescription1}
        {detailsObj.noRatingDescription2}
        {Boolean(medianMileage) && (
          <Typography className={styles['sub-description']} tag="div">
            <div>
              <RoadRegularIcon className={styles['icon']} />
              Tread Life Estimate
            </div>
            <div>
              {formatMilesInThousands(minMileage)}-
              {formatMilesInThousands(medianMileage)}
            </div>
          </Typography>
        )}
        {!hideLink && (
          <Button
            className={styles['button']}
            href={`${dataReportUrl}#treadwell-data`}
            onClick={onClick}
            size="regular"
          >
            view full report
          </Button>
        )}
      </div>
    </div>
  );
}

export default ProductFirstPriorityDetails;
